import React, { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { disputeService } from '../../services/disputeService';
import { AlertTriangle, Scale, RefreshCw } from 'lucide-react';
import DisputeStatusTabs from './components/DisputeStatusTabs';
import DisputeListTable from './components/DisputeListTable';
import DisputeDetailPanel from './components/DisputeDetailPanel';
import DisputeFilters from './components/DisputeFilters';

const AdminDisputeMediationDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const loadDisputes = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (categoryFilter) filters.category = categoryFilter;
      const data = await disputeService?.getAllDisputesAdmin(filters);
      setDisputes(data || []);
    } catch (err) {
      console.error('Error loading disputes:', err);
      setDisputes([]);
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, refreshKey]);

  useEffect(() => {
    loadDisputes();
  }, [loadDisputes]);

  const handleStatusChange = () => {
    setRefreshKey((k) => k + 1);
    setSelectedDispute(null);
  };

  const filteredDisputes = disputes?.filter((d) => {
    const matchesTab =
      activeTab === 'all' ||
      d?.disputeStatus === activeTab ||
      (activeTab === 'open' && (d?.disputeStatus === 'open' || d?.disputeStatus === 'submitted'));

    const searchLower = search?.toLowerCase();
    const matchesSearch =
      !search ||
      d?.title?.toLowerCase()?.includes(searchLower) ||
      d?.filedByProfile?.full_name?.toLowerCase()?.includes(searchLower) ||
      d?.filedByProfile?.email?.toLowerCase()?.includes(searchLower) ||
      d?.respondentProfile?.full_name?.toLowerCase()?.includes(searchLower) ||
      d?.respondentProfile?.email?.toLowerCase()?.includes(searchLower) ||
      d?.id?.toLowerCase()?.includes(searchLower);

    return matchesTab && matchesSearch;
  });

  const counts = {
    all: disputes?.length,
    open: disputes?.filter((d) => d?.disputeStatus === 'open' || d?.disputeStatus === 'submitted')?.length,
    in_mediation: disputes?.filter((d) => d?.disputeStatus === 'in_mediation' || d?.disputeStatus === 'mediator_assigned')?.length,
    resolved: disputes?.filter((d) => d?.disputeStatus === 'resolved' || d?.disputeStatus === 'closed')?.length,
    escalated: disputes?.filter((d) => d?.disputeStatus === 'escalated')?.length,
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AdminSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed((c) => !c)} />
      <main
        className={`flex-1 transition-all duration-300 ${
          sidebarCollapsed ? 'ml-20' : 'ml-64'
        }`}
      >
        {/* Page Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <Scale className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Dispute Mediation</h1>
                <p className="text-sm text-gray-500">Upravljanje sporovima i posredovanje između korisnika</p>
              </div>
            </div>
            <button
              onClick={() => setRefreshKey((k) => k + 1)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Osveži
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Disclaimer */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 mb-0.5">Napomena o neutralnosti platforme</p>
              <p className="text-sm text-amber-700">
                Platforma nije učesnik transakcija između korisnika i ne može biti strana u sporovima.
                Medijacija se pruža kao tehnička pomoć u komunikaciji između korisnika.
                U slučaju neuspešne medijacije, korisnici se upućuju na spoljno rešavanje (sud ili arbitraža).
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className={`flex gap-6 ${
            selectedDispute ? 'flex-row' : 'flex-col'
          }`}>
            {/* Left: List */}
            <div className={`${
              selectedDispute ? 'flex-1 min-w-0' : 'w-full'
            } bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden`}>
              <div className="p-5 border-b border-gray-100">
                <DisputeStatusTabs
                  activeTab={activeTab}
                  onTabChange={(tab) => { setActiveTab(tab); setSelectedDispute(null); }}
                  counts={counts}
                />
                <DisputeFilters
                  search={search}
                  onSearchChange={setSearch}
                  category={categoryFilter}
                  onCategoryChange={setCategoryFilter}
                  onClear={() => { setSearch(''); setCategoryFilter(''); }}
                />
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">
                    Prikazano <strong className="text-gray-800">{filteredDisputes?.length}</strong> od <strong className="text-gray-800">{disputes?.length}</strong> sporova
                  </p>
                </div>
              </div>
              <DisputeListTable
                disputes={filteredDisputes}
                selectedId={selectedDispute?.id}
                onSelect={setSelectedDispute}
                loading={loading}
              />
            </div>

            {/* Right: Detail Panel */}
            {selectedDispute && (
              <div className="w-[420px] flex-shrink-0 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 200px)', position: 'sticky', top: '20px' }}>
                <DisputeDetailPanel
                  dispute={selectedDispute}
                  onClose={() => setSelectedDispute(null)}
                  onStatusChange={handleStatusChange}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDisputeMediationDashboard;
